/**
 * S02 smoke-fix (2026-06-08) · `.tab-deck` — табы вместо flip-сеток.
 *
 * Genie: flip-сетки с почти пустыми фронтами вытягиваются в колонны (высота
 * фронта = высоте самого длинного бэка). Tab-deck показывает одну панель,
 * табы-буквы читаются как мнемоника (I-N-V-E-S-T · M-S-C-W · имена моделей).
 *
 * Markup contract (per sprint-02.ts REQ-05/REQ-02/REQ-09):
 *   <div class="tab-deck" data-tab-deck="invest">
 *     <div class="td-tabs" role="tablist">
 *       <button type="button" class="td-tab" role="tab" data-td="0">I<span class="sub">независимая</span></button> ×N
 *     </div>
 *     <div class="td-panes">
 *       <div class="td-pane" role="tabpanel" data-td="0">…контент…</div> ×N
 *     </div>
 *   </div>
 *
 * Контент живёт в разметке тикета (не в JS). Активная пара получает .active;
 * первый таб активируется на init. Клавиатура: стрелки влево/вправо по табам.
 *
 * Progressive degradation: без JS root не получает .td-ready — CSS показывает
 * все панели стопкой (как старые флипы, но без пустых фронтов).
 */
export function initTabDecks(root: HTMLElement): () => void {
  const decks = Array.from(root.querySelectorAll<HTMLElement>('.tab-deck'));
  const cleanups: Array<() => void> = [];

  for (const deck of decks) {
    if (deck.dataset.tdInit === '1') continue;
    deck.dataset.tdInit = '1';

    const tabs = Array.from(deck.querySelectorAll<HTMLButtonElement>('.td-tab'));
    const panes = Array.from(deck.querySelectorAll<HTMLElement>('.td-pane'));
    if (tabs.length === 0 || tabs.length !== panes.length) continue;

    const activate = (idx: number, focus = false) => {
      tabs.forEach((t, i) => {
        const on = i === idx;
        t.classList.toggle('active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      panes.forEach((p, i) => p.classList.toggle('active', i === idx));
      if (focus) tabs[idx]?.focus();
    };

    const onClick = (e: MouseEvent) => {
      const idx = tabs.indexOf(e.currentTarget as HTMLButtonElement);
      if (idx >= 0) activate(idx);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const cur = tabs.findIndex((t) => t.classList.contains('active'));
      const next = e.key === 'ArrowRight'
        ? (cur + 1) % tabs.length
        : (cur - 1 + tabs.length) % tabs.length;
      e.preventDefault();
      activate(next, true);
    };

    tabs.forEach((t) => {
      t.addEventListener('click', onClick);
      t.addEventListener('keydown', onKey);
    });
    deck.classList.add('td-ready');
    activate(0);

    cleanups.push(() => {
      tabs.forEach((t) => {
        t.removeEventListener('click', onClick);
        t.removeEventListener('keydown', onKey);
      });
      deck.classList.remove('td-ready');
      delete deck.dataset.tdInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
